import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { CareDashboardService } from '../../care-manager-portal/components/ccm-dashboard/service/care-dashboard.service';
import { TwilioService } from './twilio.service';

/**
 * Keeps the Twilio Device's access token alive for long sessions.
 *
 * The token is only fetched when a page registers the Device, so a user who
 * stays on one page (e.g. the NP dashboard during batch calling) eventually
 * holds an expired token: the Device drops its registration and incoming
 * transfers ring nowhere, falling through to voicemail. Every CHECK_INTERVAL_MS
 * this verifies the Device is registered and its token outlives the next check;
 * otherwise it fetches a fresh token and swaps it into the existing Device.
 */
@Injectable({
  providedIn: 'root',
})
export class TwilioTokenKeeperService implements OnDestroy {
  private readonly twilio = inject(TwilioService);
  private readonly careDashboardService = inject(CareDashboardService);
  private readonly ngZone = inject(NgZone);

  private static readonly CHECK_INTERVAL_MS = 10 * 60 * 1000;
  // Refresh when the token expires before the next check would run, plus slack
  // for background-tab timer throttling.
  private static readonly REFRESH_MARGIN_MS = 15 * 60 * 1000;

  private checkTimer: any = null;
  private refreshing = false;
  private subscription = new Subscription();

  start(): void {
    if (this.checkTimer) return;
    // Outside Angular's zone so the timer doesn't trigger app-wide change
    // detection (same reason as main-layout's device monitor).
    this.ngZone.runOutsideAngular(() => {
      this.checkTimer = setInterval(
        () => this.check(),
        TwilioTokenKeeperService.CHECK_INTERVAL_MS,
      );
    });
  }

  stop(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    this.refreshing = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  check(): void {
    const device = this.twilio.getDevice();
    const expiresAt = this.getTokenExpiry(device?.token);
    const isValid =
      device?.state === 'registered' &&
      expiresAt !== null &&
      expiresAt - Date.now() > TwilioTokenKeeperService.REFRESH_MARGIN_MS;

    console.log('[TWILIO TOKEN] check', {
      state: device?.state,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      isValid,
    });

    if (!isValid) this.refresh();
  }

  private refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;

    this.subscription.add(
      this.careDashboardService.getToken().subscribe({
        next: (response) => {
          this.refreshing = false;
          if (!response?.token) {
            localStorage.setItem('deviceRegistered', 'false');
            return;
          }
          this.applyToken(response.token);
        },
        error: (error) => {
          this.refreshing = false;
          console.error('[TWILIO TOKEN] refresh failed', error);
        },
      }),
    );
  }

  private applyToken(token: string): void {
    const device = this.twilio.getDevice();

    // No usable Device: build a new one (initialize skips if one is live).
    if (!device || device.state === 'destroyed') {
      this.twilio.initialize(token);
      return;
    }

    // Swap the token into the existing Device rather than creating a second
    // one — safe mid-call, and keeps listeners attached by other services.
    device.updateToken(token);
    console.log('[TWILIO TOKEN] token updated');

    if (device.state === 'unregistered' && !this.twilio.getActiveCall()) {
      device.register().catch((error: any) => {
        console.error('[TWILIO TOKEN] re-register failed', error);
        localStorage.setItem('deviceRegistered', 'false');
      });
    }
  }

  /** Reads the JWT `exp` claim (ms since epoch); null if unreadable. */
  private getTokenExpiry(token: string | null | undefined): number | null {
    if (!token) return null;
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const exp = JSON.parse(atob(payload))?.exp;
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
      return null;
    }
  }
}
